/* Share Security Packages workspace — controlled enterprise-sales disclosure.
   Draft wizard → preview → step-up generate → immutable package + drill-in detail. */
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Loader2, Share2, Copy, ShieldAlert, Eye, Lock, RefreshCw } from "lucide-react";

const inputCls = "rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cobalt/30";
const btnNavy = "px-4 py-2 rounded-full bg-navy text-white text-xs font-bold disabled:opacity-40";
const btnGhost = "px-3 py-1.5 rounded-full border border-slate-200 text-slate-500 text-[11px] font-bold hover:border-cobalt";
const err = (e) => formatApiErrorDetail(e.response?.data?.detail?.message || e.response?.data?.detail) || "Failed";
const copy = (t) => { navigator.clipboard.writeText(t); toast.success("Copied"); };
const PURPOSES = ["enterprise_procurement", "security_review", "vendor_due_diligence", "rfp_response", "partnership_evaluation", "other"];

const StatusPill = ({ s }) => {
  const map = { draft: "bg-slate-100 text-slate-500", active: "bg-emerald-50 text-emerald-700", revoked: "bg-red-50 text-red-600", expired: "bg-amber-50 text-amber-700", superseded: "bg-violet-50 text-violet-700" };
  return <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${map[s] || map.draft}`}>{s}</span>;
};

const CreateWizard = ({ onDone }) => {
  const [f, setF] = useState({ name: "", email: "", organization: "", purpose: "security_review", expires_days: 14, permission: "view", nda_required: true });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target?.type === "checkbox" ? e.target.checked : e.target.value });
  const create = async () => {
    if (!f.name || !f.email || !f.organization) { toast.error("Recipient name, email and company are required"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/security-packages", { recipient: { name: f.name, email: f.email, organization: f.organization }, purpose: f.purpose, expires_days: Number(f.expires_days), permission: f.permission, nda_required: f.nda_required });
      toast.success("Draft package created — preview and generate to issue the link");
      onDone(data.package_id);
    } catch (e) { toast.error(err(e)); } finally { setBusy(false); }
  };
  return (
    <div className="rounded-3xl bg-white border border-slate-100 p-5" data-testid="spkg-wizard">
      <h3 className="font-heading font-bold text-navy text-sm flex items-center gap-2"><Share2 className="w-4 h-4 text-cobalt" /> New Share Security Package</h3>
      <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-2">
        <input placeholder="Recipient name" value={f.name} onChange={set("name")} className={inputCls} data-testid="spkg-recipient-name" />
        <input placeholder="Recipient email" value={f.email} onChange={set("email")} className={inputCls} data-testid="spkg-recipient-email" />
        <input placeholder="Prospect company" value={f.organization} onChange={set("organization")} className={inputCls} data-testid="spkg-recipient-org" />
        <select value={f.purpose} onChange={set("purpose")} className={inputCls} data-testid="spkg-purpose">
          {PURPOSES.map((p) => <option key={p} value={p}>{p.replace(/_/g, " ")}</option>)}
        </select>
        <select value={f.expires_days} onChange={set("expires_days")} className={inputCls} data-testid="spkg-expiry">
          {[7, 14, 30, 60, 90].map((d) => <option key={d} value={d}>Expires in {d} days</option>)}
        </select>
        <select value={f.permission} onChange={set("permission")} className={inputCls} data-testid="spkg-permission">
          <option value="view">View only</option><option value="download">Controlled download</option>
        </select>
      </div>
      <div className="mt-2 flex items-center justify-between">
        <label className="flex items-center gap-1.5 text-xs text-slate-500"><input type="checkbox" checked={f.nda_required} onChange={set("nda_required")} data-testid="spkg-nda" /> Require electronic NDA</label>
        <button onClick={create} disabled={busy} className={btnNavy} data-testid="spkg-create-btn">{busy ? "Creating…" : "Create draft"}</button>
      </div>
      <p className="text-[10px] text-slate-400 mt-2">Packages use the sanitized presentation layer only — no client data, PII/PHI, internal findings, endpoint names or paths. Generation requires step-up authentication.</p>
    </div>
  );
};

const PackageDetail = ({ id, onBack, reload }) => {
  const [d, setD] = useState(null);
  const [pw, setPw] = useState("");
  const [preview, setPreview] = useState(null);
  const [share, setShare] = useState(null);
  const load = useCallback(() => api.get(`/security-packages/${id}`).then(({ data }) => setD(data)).catch((e) => toast.error(err(e))), [id]);
  useEffect(() => { load(); }, [load]);
  if (!d) return <div className="py-10 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-cobalt" /></div>;
  const act = (path, body = {}, msg = "Done") => async () => {
    try { const { data } = await api.post(`/security-packages/${id}/${path}`, body); toast.success(msg); if (data.share_path) setShare(data); load(); reload(); }
    catch (e) { toast.error(err(e)); }
  };
  const vp = d.verification_panel || {};
  return (
    <div className="space-y-3" data-testid="spkg-detail">
      <button onClick={onBack} className={btnGhost} data-testid="spkg-back">← All packages</button>
      <div className="rounded-3xl bg-white border border-slate-100 p-5">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <div className="font-heading font-bold text-navy text-sm">{d.recipient.organization} <span className="text-slate-400 font-normal">· {d.recipient.name} · {d.recipient.email}</span></div>
            <div className="text-[11px] text-slate-400 mt-0.5">{d.package_id} · v{d.version} · {d.purpose?.replace(/_/g, " ")} · watermark {d.watermark_id || "—"}</div>
          </div>
          <StatusPill s={d.effective_status} />
        </div>
        <div className="mt-3 grid grid-cols-2 md:grid-cols-6 gap-2 text-[11px]" data-testid="spkg-verification-panel">
          {[["Package integrity", vp.package_integrity], ["Evidence integrity", vp.evidence_artifact_integrity],
            ["Generated", (vp.generated_at || "—").slice(0, 10)], ["Expires", (vp.expires_at || "—").slice(0, 10)],
            ["Version", `v${vp.package_version}`], ["Last validated", (vp.last_validated_at || "—").slice(0, 16).replace("T", " ")]].map(([l, v]) => (
            <div key={l} className={`rounded-xl px-3 py-2 ${String(v) === "Failed" ? "bg-red-50 text-red-700" : "bg-slate-50 text-slate-600"}`}>
              <div className="text-[9px] font-bold uppercase text-slate-400">{l}</div><div className="font-bold">{String(v)}</div>
            </div>
          ))}
        </div>
        {vp.source_artifact_outdated && <p className="text-[11px] text-amber-600 mt-2 flex items-center gap-1" data-testid="spkg-outdated"><ShieldAlert className="w-3.5 h-3.5" /> A newer evidence artifact exists — this issued package is preserved unchanged. Issue an updated version to share current evidence.</p>}
        <div className="mt-3 flex flex-wrap gap-2 items-center">
          {d.status === "draft" && <>
            <button onClick={async () => { try { const { data } = await api.post(`/security-packages/${id}/preview`); setPreview(data.content); } catch (e) { toast.error(err(e)); } }} className={btnGhost} data-testid="spkg-preview-btn"><Eye className="w-3 h-3 inline mr-1" />Preview exact package</button>
            <input type="password" placeholder="Step-up password" value={pw} onChange={(e) => setPw(e.target.value)} className={inputCls + " !py-1.5 !text-xs"} data-testid="spkg-stepup-pw" />
            <button onClick={act("generate", { password: pw }, "Package issued — link shown once")} className={btnNavy} data-testid="spkg-generate-btn"><Lock className="w-3 h-3 inline mr-1" />Approve &amp; generate secure link</button>
          </>}
          {d.status !== "draft" && !d.revoked_at && <>
            <button onClick={act("revoke", { reason: "Revoked by administrator" }, "Link revoked")} className={btnGhost} data-testid="spkg-revoke-btn">Revoke link</button>
            <button onClick={act("extend", { days: 7 }, "Extended 7 days")} className={btnGhost} data-testid="spkg-extend-btn">Extend +7d</button>
            <button onClick={act("disable-downloads", { disabled: !d.downloads_disabled }, "Download setting updated")} className={btnGhost} data-testid="spkg-toggle-dl-btn">{d.downloads_disabled ? "Enable" : "Disable"} downloads</button>
            <input type="password" placeholder="Step-up password" value={pw} onChange={(e) => setPw(e.target.value)} className={inputCls + " !py-1.5 !text-xs"} data-testid="spkg-stepup-pw2" />
            <button onClick={act("reissue", { password: pw }, "New version issued — previous preserved as superseded")} className={btnGhost} data-testid="spkg-reissue-btn"><RefreshCw className="w-3 h-3 inline mr-1" />Issue updated package</button>
          </>}
          <button onClick={async () => { const { data } = await api.get(`/security-packages/${id}/access-export`); const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" }); const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = `${id}_access_history.json`; a.click(); }} className={btnGhost} data-testid="spkg-export-btn">Export access history</button>
          <button onClick={act("create-incident", {}, "Incident created")} className={btnGhost} data-testid="spkg-incident-btn">Create incident</button>
        </div>
        {share && (
          <div className="mt-3 rounded-xl bg-emerald-50 border border-emerald-100 p-3 text-xs" data-testid="spkg-share-link">
            <b className="text-emerald-800">Secure link (shown once — only its hash is stored):</b>
            <div className="mt-1 flex items-center gap-2 break-all text-emerald-700">{window.location.origin}{share.share_path}
              <button onClick={() => copy(`${window.location.origin}${share.share_path}`)} className="shrink-0"><Copy className="w-3.5 h-3.5" /></button></div>
          </div>
        )}
      </div>
      {preview && (
        <div className="rounded-3xl bg-white border border-slate-100 p-5" data-testid="spkg-preview">
          <h4 className="font-heading font-bold text-navy text-sm">Exact sanitized package preview</h4>
          <pre className="mt-2 text-[10px] text-slate-500 bg-slate-50 rounded-xl p-3 overflow-auto max-h-96">{JSON.stringify(preview, null, 2)}</pre>
        </div>
      )}
      <div className="grid md:grid-cols-2 gap-3">
        <div className="rounded-3xl bg-white border border-slate-100 p-5" data-testid="spkg-access-history">
          <h4 className="font-heading font-bold text-navy text-sm">Access history ({(d.access_history || []).length})</h4>
          <div className="mt-2 space-y-1 max-h-64 overflow-auto">
            {(d.access_history || []).map((a) => (
              <div key={a.log_id} className={`flex justify-between text-[11px] rounded-lg px-2 py-1 ${a.result === "success" ? "bg-slate-50 text-slate-500" : "bg-red-50 text-red-600"}`}>
                <span>{a.action} · {a.result}</span><span>{(a.at || "").slice(0, 16).replace("T", " ")} · {a.ip || "?"}</span>
              </div>
            ))}
            {!(d.access_history || []).length && <p className="text-[11px] text-slate-300">No access yet.</p>}
          </div>
        </div>
        <div className="rounded-3xl bg-white border border-slate-100 p-5" data-testid="spkg-questions">
          <h4 className="font-heading font-bold text-navy text-sm">Prospect questions</h4>
          <div className="mt-2 space-y-2 max-h-64 overflow-auto">
            {(d.questions || []).map((q) => <QuestionRow key={q.question_id} q={q} reload={load} />)}
            {!(d.questions || []).length && <p className="text-[11px] text-slate-300">No questions yet.</p>}
          </div>
        </div>
      </div>
    </div>
  );
};

const QuestionRow = ({ q, reload }) => {
  const [a, setA] = useState("");
  return (
    <div className="rounded-xl bg-slate-50 p-2.5 text-[11px]" data-testid={`spkg-question-${q.question_id}`}>
      <div className="text-slate-600"><b className="text-navy">{q.organization}:</b> {q.text}</div>
      {q.status === "answered" ? <div className="text-emerald-700 mt-1">↳ {q.answer}</div> : (
        <div className="mt-1.5 flex gap-1.5">
          <input placeholder="Answer…" value={a} onChange={(e) => setA(e.target.value)} className={inputCls + " flex-1 !py-1 !text-[11px]"} data-testid="spkg-answer-input" />
          <button onClick={async () => { try { await api.post(`/security-packages/questions/${q.question_id}/answer`, { answer: a }); toast.success("Answered"); reload(); } catch (e) { toast.error(err(e)); } }} className={btnNavy + " !py-1"} data-testid="spkg-answer-btn">Send</button>
        </div>
      )}
    </div>
  );
};

export const SharePackagesPanel = () => {
  const [d, setD] = useState(null);
  const [sel, setSel] = useState(null);
  const load = useCallback(() => api.get("/security-packages").then(({ data }) => setD(data)).catch(() => setD({ packages: [], buckets: {} })), []);
  useEffect(() => { load(); }, [load]);
  if (!d) return <div className="py-10 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-cobalt" /></div>;
  if (sel) return <PackageDetail id={sel} onBack={() => setSel(null)} reload={load} />;
  const stats = [["Draft", d.buckets.draft || 0], ["Active", d.buckets.active || 0], ["Expiring soon", d.buckets.expiring_soon || 0],
                 ["Expired", d.buckets.expired || 0], ["Revoked", d.buckets.revoked || 0], ["Superseded", d.buckets.superseded || 0],
                 ["Downloads", d.download_activity || 0], ["Suspicious 30d", d.suspicious_access_30d || 0], ["Open questions", d.unanswered_questions || 0]];
  return (
    <div className="space-y-4" data-testid="spkg-workspace">
      <div className="grid grid-cols-3 md:grid-cols-9 gap-2">
        {stats.map(([l, v]) => (
          <div key={l} className={`rounded-2xl bg-white border p-3 ${l === "Suspicious 30d" && v > 0 ? "border-red-200" : "border-slate-100"}`} data-testid={`spkg-stat-${l.toLowerCase().replace(/ /g, "-")}`}>
            <div className="text-[9px] font-bold uppercase text-slate-400">{l}</div>
            <div className="font-heading text-lg font-extrabold text-navy">{v}</div>
          </div>
        ))}
      </div>
      <CreateWizard onDone={(id) => { load(); setSel(id); }} />
      <div className="rounded-3xl bg-white border border-slate-100 p-5">
        <h3 className="font-heading font-bold text-navy text-sm">All packages</h3>
        <div className="mt-2 space-y-1.5">
          {(d.packages || []).map((p) => (
            <button key={p.package_id} onClick={() => setSel(p.package_id)} className="w-full flex flex-wrap justify-between items-center gap-2 rounded-xl bg-slate-50 hover:bg-slate-100 px-3 py-2 text-left text-xs" data-testid={`spkg-row-${p.package_id}`}>
              <span className="text-slate-600"><b className="text-navy">{p.recipient?.organization}</b> · {p.recipient?.name} · v{p.version}</span>
              <span className="flex items-center gap-2 text-slate-400">{(p.expires_at || "").slice(0, 10)} <StatusPill s={p.effective_status} /></span>
            </button>
          ))}
          {!(d.packages || []).length && <p className="text-[11px] text-slate-300">No packages yet — create the first controlled share above.</p>}
        </div>
      </div>
    </div>
  );
};
